import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  type SortChangedEvent,
} from 'ag-grid-community';
import { trendzGridTheme } from '@components/grid/trendzGridTheme';
import '@components/grid/agGridTheme.css';
import { buildForecastRunsColumns } from '../columns';
import {
  FORECAST_RUN_SORT_KEYS,
  type ForecastRun,
  type ForecastRunSortKey,
  type SortDirection,
} from '../schema';
import type { CommonResponse } from '@/types/api';

ModuleRegistry.registerModules([AllCommunityModule]);

interface Props {
  data: CommonResponse<ForecastRun> | undefined;
  onRowClick: (row: ForecastRun) => void;
  onSortChange: (sort: { sortBy?: ForecastRunSortKey; sortDir?: SortDirection }) => void;
}

const DEFAULT_COL_DEF = {
  resizable: true,
  sortable: false,
  filter: false,
  minWidth: 100,
  unSortIcon: true,
};

const SORT_KEY_SET = new Set<string>(FORECAST_RUN_SORT_KEYS);

function readSort(event: SortChangedEvent): {
  sortBy?: ForecastRunSortKey;
  sortDir?: SortDirection;
} {
  const state = event.api.getColumnState();
  const active = state.find(
    (c) => c.sort != null && c.colId && SORT_KEY_SET.has(c.colId),
  );
  if (!active || !active.sort || !active.colId) return {};
  return {
    sortBy: active.colId as ForecastRunSortKey,
    sortDir: active.sort as SortDirection,
  };
}

export function ForecastRunsGrid({ data, onRowClick, onSortChange }: Props) {
  const columnDefs = useMemo(() => buildForecastRunsColumns(), []);

  return (
    <div className="h-full w-full">
      <AgGridReact<ForecastRun>
        theme={trendzGridTheme}
        rowData={data?.rowData ?? []}
        columnDefs={columnDefs}
        defaultColDef={DEFAULT_COL_DEF}
        animateRows
        getRowId={(p) => p.data.id}
        suppressCellFocus
        suppressMultiSort
        rowHeight={56}
        // Whole-row click opens detail. Cells aren't editable so this doesn't
        // collide with any per-cell action.
        onRowClicked={(e) => e.data && onRowClick(e.data)}
        onSortChanged={(e) => onSortChange(readSort(e))}
      />
    </div>
  );
}
